import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { getClient, translateError } from "@vixio/data";
import { Button, Callout, Dialog, TextInput } from "@vixio/ui";

/**
 * 2FA TOTP (Supabase Auth / GoTrue):
 *  - cadastro do fator (QR + chave), desafio de 6 dígitos;
 *  - elevação sob demanda: quando uma RPC responde "exige 2FA", o painel abre este desafio
 *    (ou o cadastro, se a pessoa ainda não tem fator), a sessão vira aal2 e a ação é repetida.
 */

export function TotpChallengeForm({ factorId, onDone, autoFocus = true }: { factorId: string; onDone: () => void; autoFocus?: boolean }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="vx-stack"
      noValidate
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        if (!/^\d{6}$/.test(code)) {
          setError("O código tem 6 dígitos.");
          return;
        }
        setBusy(true);
        try {
          const { error: err } = await getClient().auth.mfa.challengeAndVerify({ factorId, code });
          if (err) throw err;
          onDone();
        } catch (err) {
          setError(translateError(err).message);
          setCode("");
        } finally {
          setBusy(false);
        }
      }}
    >
      <TextInput
        label="Código do autenticador"
        value={code}
        onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        className="mono"
        placeholder="000000"
        error={error}
      />
      <Button type="submit" variant="primary" loading={busy}>
        Verificar
      </Button>
    </form>
  );
}

export function TotpEnrollForm({ onDone }: { onDone: () => void }) {
  const [enroll, setEnroll] = useState<{ id: string; qr: string; secret: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const mfa = getClient().auth.mfa;
        const { data: list } = await mfa.listFactors();
        for (const f of list?.all ?? []) {
          if (f.status === "unverified") await mfa.unenroll({ factorId: f.id });
        }
        const { data, error: err } = await mfa.enroll({ factorType: "totp", friendlyName: `Autenticador ${new Date().toISOString().slice(0, 10)}` });
        if (err) throw err;
        setEnroll({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
      } catch (err) {
        setError(translateError(err).message);
      }
    })();
  }, []);

  if (error) return <Callout tone="crit">{error}</Callout>;
  if (!enroll) return <p className="muted">Preparando o cadastro</p>;
  return (
    <div className="vx-stack">
      <p className="text-2">
        1. No aplicativo autenticador (Google Authenticator, 1Password, Authy…), leia o QR ou digite a chave.
      </p>
      <div className="vx-row" style={{ alignItems: "flex-start", gap: 16 }}>
        <img
          src={enroll.qr.startsWith("data:") ? enroll.qr : `data:image/svg+xml;utf8,${encodeURIComponent(enroll.qr)}`}
          alt="QR code para cadastrar o autenticador"
          width={148}
          height={148}
          style={{ background: "#fff", padding: 6, border: "1px solid var(--border)", borderRadius: 4 }}
        />
        <div className="vx-stack" style={{ minWidth: 0, flex: 1 }}>
          <span className="vx-field__label">Chave (se não puder ler o QR)</span>
          <code className="mono" data-testid="totp-secret" style={{ overflowWrap: "anywhere" }}>
            {enroll.secret}
          </code>
        </div>
      </div>
      <p className="text-2">2. Digite o código de 6 dígitos que o aplicativo mostra.</p>
      <TotpChallengeForm factorId={enroll.id} onDone={onDone} autoFocus={false} />
    </div>
  );
}

// ---------------------------------------------------------------- elevação sob demanda

interface Pending {
  reason: string;
  resolve: (ok: boolean) => void;
}

const StepUpCtx = createContext<(reason: string) => Promise<boolean>>(async () => false);

export function StepUpProvider({ children, onElevated }: { children: ReactNode; onElevated: () => Promise<void> }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [factorId, setFactorId] = useState<string | null | undefined>(undefined);

  const stepUp = useCallback((reason: string) => {
    return new Promise<boolean>((resolve) => {
      setFactorId(undefined);
      setPending({ reason, resolve });
      getClient()
        .auth.mfa.listFactors()
        .then(({ data }) => setFactorId(data?.totp?.find((f) => f.status === "verified")?.id ?? null))
        .catch(() => setFactorId(null));
    });
  }, []);

  async function finish(ok: boolean) {
    if (ok) await onElevated();
    pending?.resolve(ok);
    setPending(null);
  }

  return (
    <StepUpCtx.Provider value={stepUp}>
      {children}
      <Dialog
        open={pending !== null}
        onOpenChange={(o) => {
          if (!o) void finish(false);
        }}
        title={factorId === null ? "Ative a verificação em duas etapas" : "Confirme com o seu autenticador"}
        description={pending?.reason}
      >
        {factorId === undefined ? (
          <p className="muted">Verificando</p>
        ) : factorId === null ? (
          <div className="vx-stack">
            <Callout tone="info">Operações sensíveis exigem 2FA. Cadastre um autenticador agora; a ação continua em seguida.</Callout>
            <TotpEnrollForm onDone={() => void finish(true)} />
          </div>
        ) : (
          <TotpChallengeForm factorId={factorId} onDone={() => void finish(true)} />
        )}
      </Dialog>
    </StepUpCtx.Provider>
  );
}

export function useStepUp() {
  return useContext(StepUpCtx);
}
